<template>
  <div class="inschrijven-page-container">
    <div class="page-header">
      <div class="container">
        <h1>Inschrijven als nieuwe patiënt</h1>
        <p class="header-subtitle">
          Welkom bij Tandzorg Gestel! Schrijf u gemakkelijk in als nieuwe patiënt
        </p>
      </div>
    </div>
    
    <div class="form-container">
      <div class="container">
        <div class="inschrijf-form-wrapper">
          <div class="form-info">
            <h3>Wat kunt u verwachten?</h3>
            <ul class="expectations-list">
              <li>✓ Persoonlijke aandacht en zorg</li>
              <li>✓ Moderne technieken en behandelingen</li>
              <li>✓ Ervaren tandartsen en mondhygiënisten</li>
              <li>✓ Transparante communicatie</li>
              <li>✓ Comfortabele praktijkomgeving</li>
            </ul>
            <div class="contact-info">
              <h4>Vragen?</h4>
              <p>📞 <a href="tel:+31735515644" class="contact-link">073 551 5644</a></p>
              <p>✉️ <a href="mailto:info@tandzorggestel.nl" class="contact-link">info@tandzorggestel.nl</a></p>
            </div>
          </div>
          
          <div class="form-section">
            <h3>Inschrijfformulier</h3>
            
            <!-- Test knop voor directe toegang tot bedankpagina -->
            <div class="test-section" style="text-align: center; margin-bottom: 2rem;">
              <button @click="goToBedankt" class="test-btn" :class="{ 'subtle': !userInteracted }">
                {{ userInteracted ? '🎉 Ga naar Bedankpagina' : '🔗 Direct naar Bedankpagina' }}
              </button>
              <p style="font-size: 1.1rem; color: #6c757d; margin-top: 0.5rem;">
                {{ userInteracted ? 'Klik hier als u uw inschrijving heeft voltooid' : 'Alleen gebruiken als u al bent ingeschreven' }}
              </p>
            </div>
            
            <div class="iframe-container">
              <iframe 
                ref="inschrijfIframe"
                class="inschrijf-frame" 
                :src="iframeUrl" 
                width="100%" 
                height="800px" 
                frameborder="0"
                title="Inschrijfformulier nieuwe patiënt"
                @load="onIframeLoad">
              </iframe>
              
              <!-- Fallback knop die verschijnt na formulier invullen -->
              <div v-if="showThankYouButton" class="thank-you-prompt">
                <div class="prompt-content">
                  <h4>✅ Formulier succesvol verzonden!</h4>
                  <p>Klik hieronder om door te gaan naar de bevestigingspagina.</p>
                  <button @click="goToBedankt" class="btn-success">
                    Ga naar bevestiging →
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "Inschrijven",
  data() {
    return {
      iframeUrl: this.buildIframeUrl(),
      userInteracted: false
    }
  },
  methods: {
    buildIframeUrl() {
      const baseUrl = 'https://internetagenda.vertimart.nl/inschrijven?id=990236'
      const currentDomain = window.location.origin
      const successUrl = `${currentDomain}/bedankt`
      
      // Probeer verschillende parameter namen die Vertimart mogelijk ondersteunt
      return `${baseUrl}&redirect_url=${encodeURIComponent(successUrl)}&success_url=${encodeURIComponent(successUrl)}&return_url=${encodeURIComponent(successUrl)}`
    },
    
    onIframeLoad() {
      // Start alleen detectie van gebruikersinteractie
      this.detectUserInteraction()
    },
    
    detectUserInteraction() {
      // Luister naar mouse en keyboard events op het iframe
      const iframe = this.$refs.inschrijfIframe
      
      if (iframe) {
        // Probeer te detecteren wanneer er interactie is met het iframe
        // Helaas kunnen we vanwege CORS niet direct in het iframe kijken,
        // maar we kunnen wel detecteren wanneer de iframe focus krijgt
        iframe.addEventListener('mouseenter', this.onUserInteraction)
        iframe.addEventListener('focus', this.onUserInteraction)
        
        // Ook luisteren naar click events op het iframe element
        iframe.addEventListener('click', this.onUserInteraction)
      }
    },
    
    onUserInteraction() {
      if (!this.userInteracted) {
        this.userInteracted = true
        console.log('Gebruiker heeft interactie gestart met het formulier')
        
        // Alleen de knop tekst veranderen - geen timers meer
      }
    },
    
    goToBedankt() {
      this.$router.push('/bedankt')
    }
  },
  mounted() {
    document.title = "Inschrijven | Tandzorg Gestel | Nieuwe Patiënt Worden"
    document.querySelector('meta[name="description"]').setAttribute("content", "Schrijf u in als nieuwe patiënt bij Tandzorg Gestel in Sint-Michielsgestel. Gemakkelijk online inschrijven voor kwaliteitsvolle tandheelkundige zorg.");
    this.$store.commit('updateActivePage', 'Inschrijven')
    
    // Luister naar berichten van de iframe
    window.addEventListener('message', this.handleIframeMessage)
    
    // Start GEEN automatische fallback timer meer - alleen na interactie
    // this.startFallbackTimer() - VERWIJDERD
    
    // Intersection Observer voor scroll animaties
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if(entry.isIntersecting){
          entry.target.classList.add('show')
        } 
      })
    })

    const hiddenElements = document.querySelectorAll('.hidden')
    hiddenElements.forEach((el) => observer.observe(el))
  },
  beforeUnmount() {
    // Cleanup event listener
    window.removeEventListener('message', this.handleIframeMessage)
    
    // Clear all timers
    if (this.fallbackTimer) {
      clearTimeout(this.fallbackTimer)
    }
    if (this.interactionTimer) {
      clearTimeout(this.interactionTimer)
    }
    
    // Remove iframe event listeners
    const iframe = this.$refs.inschrijfIframe
    if (iframe) {
      iframe.removeEventListener('mouseenter', this.onUserInteraction)
      iframe.removeEventListener('focus', this.onUserInteraction)
      iframe.removeEventListener('click', this.onUserInteraction)
    }
  }
}
</script>

<style scoped>
.inschrijven-page-container {
  margin-top: 88px;
  min-height: calc(100vh - 88px);
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.page-header {
  background: linear-gradient(135deg, #00b8b8 0%, #0891b2 100%);
  color: white;
  padding: 4rem 2rem;
  text-align: center;
}

.page-header h1 {
  font-size: 3.5rem;
  margin-bottom: 1rem;
  font-weight: 600;
}

.header-subtitle {
  font-size: 1.8rem;
  max-width: 700px;
  margin: 0 auto;
  opacity: 0.9;
  line-height: 1.5;
}

.form-container {
  padding: 4rem 0;
}

.inschrijf-form-wrapper {
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 4rem;
  max-width: 1400px;
  margin: 0 auto;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  overflow: hidden;
}

.form-info {
  padding: 3rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.form-info h3 {
  color: var(--blue);
  font-size: 2.4rem;
  margin-bottom: 2rem;
  font-weight: 600;
}

.expectations-list {
  list-style: none;
  padding: 0;
  margin-bottom: 3rem;
}

.expectations-list li {
  font-size: 1.6rem;
  margin-bottom: 1rem;
  color: #495057;
  line-height: 1.5;
}

.contact-info {
  background: white;
  padding: 2rem;
  border-radius: 8px;
  border-left: 4px solid var(--blue);
}

.contact-info h4 {
  color: var(--blue);
  margin-bottom: 1rem;
  font-size: 1.8rem;
}

.contact-info p {
  margin-bottom: 0.5rem;
  font-size: 1.4rem;
  color: #495057;
}

.form-section {
  padding: 3rem;
}

.form-section h3 {
  color: var(--blue);
  font-size: 2.4rem;
  margin-bottom: 2rem;
  font-weight: 600;
  text-align: center;
}

.inschrijf-frame {
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.iframe-container {
  position: relative;
}

.thank-you-prompt {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.3);
  z-index: 10;
  max-width: 400px;
  width: 90%;
}

.prompt-content {
  padding: 2rem;
  text-align: center;
}

.prompt-content h4 {
  color: #28a745;
  font-size: 1.8rem;
  margin-bottom: 1rem;
}

.prompt-content p {
  color: #6c757d;
  font-size: 1.4rem;
  margin-bottom: 2rem;
}

.btn-success {
  background: #28a745;
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-size: 1.6rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-success:hover {
  background: #218838;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
}

.test-btn {
  background: var(--blue);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-size: 1.4rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.test-btn:hover {
  background: #0056b3;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
}

.test-btn.subtle {
  background: #6c757d;
  font-size: 1.2rem;
  padding: 0.8rem 1.5rem;
  opacity: 0.7;
}

.test-btn.subtle:hover {
  background: #5a6268;
  opacity: 1;
}

/* Responsive design */
@media (max-width: 1024px) {
  .inschrijf-form-wrapper {
    grid-template-columns: 1fr;
    gap: 0;
    margin: 0 1rem;
  }
  
  .form-info {
    order: 2;
  }
  
  .form-section {
    order: 1;
  }
}

@media (max-width: 768px) {
  .inschrijven-page-container {
    margin-top: 55px;
  }
  
  .page-header {
    padding: 3rem 1rem;
  }
  
  .page-header h1 {
    font-size: 2.8rem;
  }
  
  .header-subtitle {
    font-size: 1.6rem;
  }
  
  .form-container {
    padding: 2rem 0;
  }
  
  .form-info,
  .form-section {
    padding: 2rem;
  }
  
  .inschrijf-frame {
    height: 700px;
  }
}

/* Animation classes */
.hidden {
  opacity: 0;
  transform: translateY(30px);
  transition: all 1s ease;
}

.show {
  opacity: 1;
  transform: translateY(0);
}
</style>
